import { z } from 'zod';
import { MAX_OFFSET_MINUTES, MAX_DURATION_MINUTES } from './timeline';

// 5 minutes, not 0: a zero-length window is not a window, and 5 is one snap
// step — the smallest increment the timeline's own drag-and-drop deals in.
const DURATION_TOO_SHORT_MESSAGE = 'A coverage window needs to run for at least one snap step';
const DURATION_TOO_LONG_MESSAGE = 'An item cannot run longer than a day';

const offsetMinutes = z
  .number()
  .int()
  .min(-MAX_OFFSET_MINUTES, 'That is more than two days before the wedding starts')
  .max(MAX_OFFSET_MINUTES, 'That is more than two days after the wedding starts');

/**
 * A coverage window is not a timeline item, and `offsetMinutes` and
 * `durationMinutes` are where that shows up in the wire shape.
 *
 * `offsetMinutes` is bounded the same way a timeline item's is — it is drawn
 * on the same axis, dragged with the same clamp, and a window that could sit
 * somewhere the axis cannot draw it is the same bug `MAX_OFFSET_MINUTES` was
 * written to prevent there.
 *
 * `durationMinutes` is NOT the timeline item's field reused. A timeline item's
 * duration may be null because it can run to the next item, or to the end of
 * the night — there is always something after it to borrow a length from, or
 * to be last against. A coverage window has no "next window" to run to and no
 * place in that chain at all; nothing else in the data model supplies its
 * length if it does not state one. So the field here is required and
 * non-nullable, min 5 — one snap step — rather than optional-nullable like its
 * timeline counterpart.
 */
export const coverageAnnotationFields = z.object({
  category: z.string().trim().min(1).max(100).nullable(),
  label: z.string().trim().min(1).max(120).nullable(),
  offsetMinutes,
  durationMinutes: z
    .number()
    .int()
    .min(5, DURATION_TOO_SHORT_MESSAGE)
    .max(MAX_DURATION_MINUTES, DURATION_TOO_LONG_MESSAGE),
});

/**
 * Exactly one of `category` or `label` is set, never both and never neither.
 *
 * This is the rule that makes a window either a merge variable or a note, and
 * never something in between. A category-tagged window is how `coverageByCategory`
 * finds "the photographer's hours" to hand to `{{coverage_hours}}`; free text
 * is for a window — "Venue access" — that names a constraint with nothing to
 * resolve it against. Letting both be set would leave the resolver a tie-break
 * to invent; letting neither be set would store a window that cannot be
 * rendered into an email or read on the timeline as a note.
 *
 * `undefined` is deliberately not treated as a tag here — see `coverageHasExactlyOneTag`
 * below for why that matters on a PATCH.
 */
const isTag = (value: string | null | undefined): value is string =>
  typeof value === 'string';

/**
 * A request that touches either tag names both of them.
 *
 * Without this, the pair rule is unjudgeable at the parse boundary and the
 * database is left to enforce it — which it does, by raising on
 * `coverage_one_tag` and turning an ordinary request into a 500 carrying a
 * constraint name. Two reachable requests do it:
 *
 *  - `{ category: null }` against a category-tagged window clears the only tag
 *    it has, leaving a row with neither.
 *  - `{ category: 'Florist' }` against a free-text window adds a second tag
 *    beside the label already stored, leaving a row with both.
 *
 * Neither can be caught by looking at the payload alone, because what they do
 * depends on what is already in the row. The alternative to this rule is
 * reading the row in the route and merging before checking — a round trip to
 * re-derive something the client already knows, since it is editing a window it
 * has loaded. So retagging says outright which tag survives and which is
 * cleared, and the pair rule stays checkable where every other rule here is.
 *
 * A PATCH that touches neither tag — `{ durationMinutes: 300 }` — is unaffected
 * and needs to resend nothing.
 *
 * Exported, with its message, for the same reason `timelineItemHasChanges` is:
 * the route composes its own schema — it has to, because a planner sends clock
 * times where this schema takes an offset and a length — and a rule restated
 * there is a rule that can drift from the one the table enforces.
 */
export const coverageTagsNamedTogether = (v: {
  category?: string | null;
  label?: string | null;
}) => (v.category === undefined) === (v.label === undefined);
export const coverageTagsTogetherMessage =
  'Retagging a window sets both the category and the label';

/**
 * Exactly one tag is a real string, given that both were named.
 *
 * Shared between create and update because the pair rule is one rule, not two.
 * On create both keys are always present — the fields are required, merely
 * nullable — and on update `coverageTagsNamedTogether` above is what guarantees it, so
 * by the time this runs there is always enough in the payload to judge. The
 * `undefined` case it still has to allow is the PATCH that names neither tag;
 * that one is the empty-patch rule's business, not this one's.
 */
export const coverageHasExactlyOneTag = (v: {
  category?: string | null;
  label?: string | null;
}) => {
  const present = [v.category, v.label].filter((value) => value !== undefined);
  if (present.length === 0) return true;
  return present.filter(isTag).length === 1;
};
export const coverageOneTagMessage =
  'Tag the window with a category or a label, not both and not neither';

/**
 * Two nullable fields plus a refine, not a discriminated union on a `kind`
 * column. Two reasons, both load-bearing:
 *
 * 1. The table this schema fronts states the rule the same way — a
 *    `CHECK ((category IS NULL) <> (label IS NULL))` constraint, added in the
 *    next task — so the Zod shape and the Postgres shape agree on what a row
 *    looks like rather than one of them translating into the other.
 * 2. A PATCH has to be able to swap one tag for the other — "this was a
 *    free-text 'Venue access' window, make it the Florist's window instead" —
 *    in a single request. A discriminated union would make that request two
 *    different shapes depending on which way the swap goes; two plain
 *    nullable fields make it one PATCH with both keys set.
 */
export const createCoverageAnnotationSchema = coverageAnnotationFields.refine(
  coverageHasExactlyOneTag,
  { message: coverageOneTagMessage },
);

/**
 * Whether a PATCH actually asks for something — the same idiom as
 * `timelineItemHasChanges` in `./timeline`, and for the same reason:
 * `Object.values`, not `Object.keys`, because a key Zod never saw is dropped
 * from the parsed output rather than kept as an explicit `undefined`, so
 * every key that survives here carries a real value, `null` included.
 */
const hasChanges = (v: object) => Object.values(v).some((value) => value !== undefined);
const EMPTY_PATCH_MESSAGE = 'Nothing to change';

/** Every field optional, but at least one present, and the pair rule still holds. */
export const updateCoverageAnnotationSchema = coverageAnnotationFields
  .partial()
  .refine(hasChanges, { message: EMPTY_PATCH_MESSAGE })
  .refine(coverageTagsNamedTogether, { message: coverageTagsTogetherMessage })
  .refine(coverageHasExactlyOneTag, { message: coverageOneTagMessage });

export type CreateCoverageAnnotation = z.infer<typeof createCoverageAnnotationSchema>;
export type UpdateCoverageAnnotation = z.infer<typeof updateCoverageAnnotationSchema>;
